// Google Identity Services (GIS) の薄いラッパ
// 設計書 §3.4.1 + CLAUDE.md「Google OAuth 2要素フロー拡張」
//
// `https://accounts.google.com/gsi/client` を <script> タグで動的に読み込み、
// `window.google.accounts.id` API 経由で id_token を取得する。
// `@react-oauth/google` のような追加依存は使わず、素の GIS を呼ぶ。
//
// 使い方:
//   const idToken = await requestGoogleIdToken({ clientId });
//   // idToken を /v1/auth/google に POST する。
//
// 内部実装メモ:
// - GIS の `prompt()` は One Tap UI を出し、ユーザーが選択すると callback で
//   credential (= ID Token) が返る。
// - One Tap がブロックされる(連続スキップ・第三者 Cookie 不可など)場合、
//   `notification.isNotDisplayed() / .isSkippedMoment()` を検出してエラーに
//   変換するが、UX を担保するため `renderButton()` 経路を主にする。
// - このファイルでは hook ではなく Promise を返す関数を export し、
//   呼び出し側(LoginPage)が任意のタイミングで呼べるようにする。

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

let scriptLoadPromise: Promise<void> | null = null;

interface GisCredentialResponse {
  credential: string;
  select_by?: string;
}

interface GisIdConfig {
  client_id: string;
  callback: (response: GisCredentialResponse) => void;
  // ITP(Safari)対応や One Tap UI のチューニング用。今は最小限。
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  ux_mode?: 'popup' | 'redirect';
  context?: 'signin' | 'signup' | 'use';
}

interface GisButtonConfig {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  width?: number;
  logo_alignment?: 'left' | 'center';
  locale?: string;
}

interface GisAccountsId {
  initialize: (config: GisIdConfig) => void;
  prompt: (
    momentListener?: (notification: {
      isNotDisplayed: () => boolean;
      isSkippedMoment: () => boolean;
      isDismissedMoment: () => boolean;
      getNotDisplayedReason: () => string | undefined;
      getSkippedReason: () => string | undefined;
      getDismissedReason: () => string | undefined;
    }) => void,
  ) => void;
  renderButton: (parent: HTMLElement, options: GisButtonConfig) => void;
  cancel: () => void;
  disableAutoSelect: () => void;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: GisAccountsId;
      };
    };
  }
}

export class GoogleIdentityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GoogleIdentityError';
  }
}

// `https://accounts.google.com/gsi/client` を 1 度だけ <script> として読み込む。
// 2 度目以降は同じ Promise を返してキャッシュ(冪等)。
export function loadGoogleIdentityScript(): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new GoogleIdentityError('document が利用できない環境です'));
      return;
    }
    // 既に読み込まれている可能性をチェック(別経路で挿入されたケース)
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${GIS_SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new GoogleIdentityError('Google Identity Services スクリプトの読み込みに失敗しました')),
      );
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new GoogleIdentityError('Google Identity Services スクリプトの読み込みに失敗しました'));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

// 指定の DOM 要素に「Sign in with Google」ボタンをレンダリングする。
// クリックされたら callback に id_token (= credential) が渡る。
//
// 戻り値の cleanup() を呼ぶと callback への参照を破棄する(unmount 用)。
// GIS 自体には button の destroy API が無いため、container を innerHTML="" で
// クリアする副作用も含む。
export async function renderGoogleButton(opts: {
  clientId: string;
  container: HTMLElement;
  onCredential: (idToken: string) => void;
  onError?: (error: GoogleIdentityError) => void;
  buttonWidth?: number;
}): Promise<() => void> {
  await loadGoogleIdentityScript();
  const accountsId = window.google?.accounts?.id;
  if (!accountsId) {
    const err = new GoogleIdentityError('Google Identity Services が利用できません');
    opts.onError?.(err);
    throw err;
  }

  let cancelled = false;

  accountsId.initialize({
    client_id: opts.clientId,
    callback: (response) => {
      if (cancelled) return;
      if (!response.credential) {
        opts.onError?.(new GoogleIdentityError('Google から credential が返りませんでした'));
        return;
      }
      opts.onCredential(response.credential);
    },
    auto_select: false,
    cancel_on_tap_outside: true,
    ux_mode: 'popup',
    context: 'signin',
  });

  // ボタンを描画(高さ・幅は親コンテナに合わせる)
  accountsId.renderButton(opts.container, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'signin_with',
    shape: 'rectangular',
    logo_alignment: 'left',
    width: opts.buttonWidth ?? 304,
    locale: 'ja',
  });

  return () => {
    cancelled = true;
    try {
      accountsId.cancel();
    } catch {
      // no-op
    }
    if (opts.container.isConnected) {
      opts.container.innerHTML = '';
    }
  };
}
